import { Layers, Loader2, PackageOpen } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/overlays";
import { Checkbox, ScrollArea } from "@/components/ui/primitives";
import { trackJob } from "@/hooks/use-upload";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useActivity } from "@/stores/activity";
import type { ExportInventory } from "@graphmind/api-client";

/** Human labels for the content tiers the backend exposes (ADR-0006). The
 * manifest, agent brief and README are always written, so they aren't listed. */
const TIER_LABELS: Record<string, { label: string; hint: string }> = {
  metadata: { label: "Metadata", hint: "Paper card, citation, structure" },
  summaries: { label: "Summaries", hint: "Abstract, claims, methods, limitations" },
  selective_context: { label: "Agent routing", hint: "Brief + retrieval / page / chunk maps" },
  extracted_content: { label: "Extracted content", hint: "Section / figure / table text" },
  raw: { label: "Raw", hint: "Full text + original file (large)" },
};

const IMPORTANCE_TONE: Record<string, string> = {
  high: "text-primary",
  medium: "text-muted-foreground",
  low: "text-muted-foreground",
};

function fmtTokens(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}

/** Per-document tiered export (ADR-0006): the user picks which tiers and
 * sections to include and sees the rough token cost before exporting a smart,
 * agent-addressable ZIP. */
export function TieredExportDialog({
  open,
  onOpenChange,
  docId,
  title,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  docId: string;
  title: string;
}) {
  const [inv, setInv] = useState<ExportInventory | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tiers, setTiers] = useState<Set<string>>(new Set());
  const [sections, setSections] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) {
      setInv(null);
      setError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    api
      .exportInventory(docId)
      .then((data) => {
        if (cancelled) return;
        setInv(data);
        setTiers(new Set(data.tiers ?? [])); // everything on by default
        setSections(new Set((data.sections ?? []).map((s) => s.id)));
      })
      .catch((err: unknown) => {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, docId]);

  const selectedTokens = useMemo(
    () =>
      (inv?.sections ?? [])
        .filter((s) => sections.has(s.id))
        .reduce((sum, s) => sum + s.token_estimate, 0),
    [inv, sections],
  );

  const toggle = (set: Set<string>, key: string, setter: (s: Set<string>) => void) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    setter(next);
  };

  const run = async () => {
    if (!inv) return;
    setBusy(true);
    try {
      const invSecs = inv.sections ?? [];
      const selectedIds = invSecs.filter((s) => sections.has(s.id)).map((s) => s.id);
      const selection = {
        tiers: [...tiers],
        // Empty = "all sections"; only send a subset when one is deselected.
        section_ids: selectedIds.length === invSecs.length ? [] : selectedIds,
        include_raw: tiers.has("raw"),
      };
      const { job_id } = await api.createExport({
        kind: "tiered_bundle",
        document_id: docId,
        selection,
      });
      useActivity.getState().upsert({
        id: job_id,
        kind: "export",
        title: `Tiered export · ${title}`,
        status: "queued",
      });
      onOpenChange(false);
      toast(`Building tiered export of “${title}”…`, {
        description: "Summarizing and packaging the document.",
      });
      const job = await trackJob(job_id);
      if (job.status === "done") {
        toast.success("Tiered export ready", {
          duration: 12_000,
          action: {
            label: "Download",
            onClick: () => window.open(`/api/exports/${job_id}/download`, "_blank"),
          },
        });
      } else {
        toast.error("Tiered export failed", { description: job.error ?? "" });
      }
    } catch (err) {
      toast.error("Couldn't start the tiered export", {
        description: err instanceof Error ? err.message : String(err),
      });
    } finally {
      setBusy(false);
    }
  };

  const secs = inv?.sections ?? [];
  const tierKeys = inv?.tiers ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="Smart tiered export" className="w-[min(640px,92vw)]">
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-lg border border-border bg-background p-3">
            <Layers className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
            <p className="text-xs leading-relaxed text-muted-foreground">
              A layered, agent-addressable ZIP — manifest, summaries, routing maps and
              extracted content — so an AI agent reads only what it needs. Pick the tiers
              and sections to include; the raw full text is the largest piece.
            </p>
          </div>

          {loading && (
            <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Reading document contents…
            </div>
          )}
          {error && <p className="py-4 text-sm text-rose-400">Couldn&apos;t load contents: {error}</p>}

          {inv && !loading && (
            <>
              <div>
                <p className="mb-2 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
                  Tiers
                </p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-2">
                  {tierKeys.map((tier) => {
                    const meta = TIER_LABELS[tier] ?? { label: tier, hint: "" };
                    return (
                      <div key={tier} className="flex flex-col">
                        <Checkbox
                          checked={tiers.has(tier)}
                          onCheckedChange={() => toggle(tiers, tier, setTiers)}
                          label={meta.label}
                        />
                        {meta.hint && (
                          <span className="ml-6 text-[10px] text-muted-foreground">{meta.hint}</span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              {secs.length > 0 && (
                <div>
                  <div className="mb-2 flex items-center justify-between">
                    <p className="font-mono text-[10px] uppercase tracking-wide text-muted-foreground">
                      Sections ({sections.size}/{secs.length})
                    </p>
                    <button
                      type="button"
                      className="text-[10px] text-primary hover:underline"
                      onClick={() =>
                        setSections(
                          sections.size === secs.length
                            ? new Set()
                            : new Set(secs.map((s) => s.id)),
                        )
                      }
                    >
                      {sections.size === secs.length ? "Deselect all" : "Select all"}
                    </button>
                  </div>
                  <ScrollArea className="max-h-52 rounded-lg border border-border">
                    <div className="divide-y divide-border/60">
                      {secs.map((s) => (
                        <div key={s.id} className="flex items-center gap-2 px-3 py-1.5">
                          <Checkbox
                            checked={sections.has(s.id)}
                            onCheckedChange={() => toggle(sections, s.id, setSections)}
                            label=""
                          />
                          <span
                            className="min-w-0 flex-1 truncate text-xs text-foreground"
                            style={{ paddingLeft: `${Math.max(0, (s.level - 1) * 12)}px` }}
                            title={s.title}
                          >
                            {s.title}
                          </span>
                          <span className={cn("text-[10px]", IMPORTANCE_TONE[s.importance] ?? "")}>
                            {s.importance}
                          </span>
                          <span className="w-12 text-right font-mono text-[10px] text-muted-foreground">
                            ~{fmtTokens(s.token_estimate)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}

              <div className="flex items-center justify-between gap-3 border-t border-border pt-3">
                <span className="font-mono text-[10px] text-muted-foreground">
                  ~{fmtTokens(selectedTokens)} tokens of source text selected
                </span>
                <Button type="button" variant="default" disabled={busy || tiers.size === 0} onClick={() => void run()}>
                  <PackageOpen className="h-3.5 w-3.5" />
                  {busy ? "Building…" : "Build tiered export"}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
